import { useEffect, useMemo, useState } from 'react';
import type { CwWorkRoleAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwWorkRoleAliases, useSetCwWorkRoleAliases } from './api.js';

interface AliasRow {
  /** Stable client-side key so rows keep focus while editing. */
  id: string;
  cwWorkRoleName: string;
  serviceCategoryId: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `work-role-alias-${aliasSeq}`;
}

interface WorkRoleAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise work role (a dropdown) onto a PSA service category (a dropdown).
 * This single mapping drives BOTH: (1) the time-entries sync — a synced time entry's
 * service category is resolved from its CW work role through these rows (support/agreement
 * hours; project hours use the project default); and (2) the agreements sync — each
 * work-role rate on a CW agreement is written as an agreement service-category rate
 * override. A CW work role without a row here has no mapping, so a time entry that needs
 * it FAILS the sync with an error (no silent default).
 */
export function WorkRoleAliasEditor({ isOpen }: WorkRoleAliasEditorProps): JSX.Element {
  const configQuery = useCwWorkRoleAliases(isOpen);
  const saveAliases = useSetCwWorkRoleAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwWorkRoleName: alias.cwWorkRoleName,
        serviceCategoryId: alias.serviceCategoryId,
      })),
    );
  }, [config]);

  const serviceCategories = config?.serviceCategories ?? [];
  const discoveredWorkRoles = config?.discoveredWorkRoles ?? [];
  const live = config?.live ?? false;

  // CW work-role names already mapped, so we can hint which discovered ones remain.
  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwWorkRoleName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredWorkRoles.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const addRow = (cwWorkRoleName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwWorkRoleName, serviceCategoryId: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwWorkRoleAliasDto[] = rows
      .filter((row) => row.cwWorkRoleName.trim() && row.serviceCategoryId)
      .map((row) => ({
        cwWorkRoleName: row.cwWorkRoleName.trim(),
        serviceCategoryId: row.serviceCategoryId,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-work-roles';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Work role rates (override)</h3>
        <p className="mt-1 text-sm text-slate-500">
          ConnectWise agreements carry per-work-role rates. Map each ConnectWise work role to a PSA
          service category below. On sync, each work-role rate on an agreement is written as that
          agreement&apos;s service-category override rate — ConnectWise work roles without a row
          here are skipped.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading work roles…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load work roles.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise work roles loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise work roles discovered yet. Configure the connection to load suggestions
              — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredWorkRoles.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the work-role aliases. Make sure each row targets a valid service
              category.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No work-role aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise work role</th>
                  <th className="py-2 pr-3 font-medium">PSA service category</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwWorkRoleName}
                        placeholder="e.g. Senior Engineer"
                        onChange={(e) => updateRow(row.id, { cwWorkRoleName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.serviceCategoryId}
                        onChange={(e) => updateRow(row.id, { serviceCategoryId: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select category —</option>
                        {serviceCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save work roles
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
